/**
 * Execution Timeline Resolver — Phase 2a (Operational Observation)
 * ────────────────────────────────────────────────────────────────
 * Pure read over existing append-only sinks. Folds Phase 1a/1b/1c
 * events for a single job into a chronological visual timeline.
 *
 * Hard rules (Phase 2a):
 *   • READ-ONLY. No writers. No new persisted entities.
 *   • Append-only — every event already exists in canonical sinks.
 *     This module only joins and orders them.
 *   • Never expose contractValue, payoutAmount, customer PII.
 *   • Source-of-truth pointers:
 *       - planningReview          → src/data/types.ts (Job.planningReview)
 *       - installerChange[]       → src/data/types.ts (Job.installerChange)
 *       - callbacks               → AppDataContext.callbacks
 *       - holdbackLedger          → AppDataContext.holdbackLedger
 *       - disputeEvents           → AppDataContext.disputeEvents
 *       - assigned + scheduled    → Job.assignedSubcontractorId / scheduledInstallDate
 */

import type { Job } from "@/data/types";
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackLedgerEntry } from "@/data/contractor/networkExecution/holdback/ledger";
import type { DisputeEvent } from "@/data/contractor/networkExecution/dispute/dispute";

export type ExecutionTimelineEventKind =
  | "review_approved"
  | "installer_assigned"
  | "installer_changed"
  | "install_scheduled"
  | "callback_filed"
  | "holdback_created"
  | "holdback_released"
  | "holdback_paused"
  | "holdback_resumed"
  | "holdback_reversed"
  | "dispute_opened"
  | "dispute_evidence_added"
  | "dispute_withdrawn"
  | "dispute_resolved";

export interface ExecutionTimelineEvent {
  id: string;
  jobId: string;
  kind: ExecutionTimelineEventKind;
  at: string;
  /** Free-form actor id from the source event. UI label is generic. */
  actorId?: string;
  /** Short, sanitized human-readable label. NEVER pricing or PII. */
  label: string;
  /** Optional secondary detail. Sanitized. */
  detail?: string;
}

export interface BuildExecutionTimelineInput {
  job: Job;
  callbacks: ReadonlyArray<Callback>;
  holdbackLedger: ReadonlyArray<HoldbackLedgerEntry>;
  disputeEvents: ReadonlyArray<DisputeEvent>;
}

const FORBIDDEN_KEYS = [
  "contractValue",
  "payoutAmount",
  "customerEmail",
  "customerPhone",
  "ssn",
] as const;

function sanitize(s: string | undefined): string | undefined {
  if (!s) return undefined;
  let out = s;
  for (const k of FORBIDDEN_KEYS) {
    out = out.replace(new RegExp(`\\b${k}\\b`, "gi"), "[redacted]");
  }
  return out;
}

export function buildExecutionTimeline(
  input: BuildExecutionTimelineInput,
): ExecutionTimelineEvent[] {
  const { job, callbacks, holdbackLedger, disputeEvents } = input;
  const events: ExecutionTimelineEvent[] = [];

  // 1) Planning review approved
  if (job.planningReview?.status === "approved" && job.planningReview.approvedAt) {
    events.push({
      id: `${job.id}-review-approved`,
      jobId: job.id,
      kind: "review_approved",
      at: job.planningReview.approvedAt,
      actorId: job.planningReview.approvedBy,
      label: "Project review approved",
    });
  }

  // 2) Installer changes (already append-only history)
  (job.installerChange ?? []).forEach((c, i) => {
    const isFirstAssign = c.fromContractorId == null && c.toContractorId != null;
    events.push({
      id: `${job.id}-icx-${i}`,
      jobId: job.id,
      kind: isFirstAssign ? "installer_assigned" : "installer_changed",
      at: c.at,
      actorId: c.actor,
      label: isFirstAssign ? "Installer assigned" : "Installer changed",
      detail: sanitize(c.reason),
    });
  });

  // 3) Install scheduled (single derivable point — only if a date exists)
  if (job.scheduledInstallDate) {
    events.push({
      id: `${job.id}-scheduled`,
      jobId: job.id,
      kind: "install_scheduled",
      at: job.scheduledInstallDate,
      label: "Install scheduled",
    });
  }

  // 4) Callbacks
  callbacks
    .filter((cb) => cb.jobId === job.id)
    .forEach((cb) => {
      events.push({
        id: cb.id,
        jobId: job.id,
        kind: "callback_filed",
        at: cb.filedAt,
        actorId: cb.filedBy,
        label: `Callback filed (${cb.type}, ${cb.severity})`,
        detail: sanitize(cb.description),
      });
    });

  // 5) Holdback ledger
  const holdbackKindMap: Record<HoldbackLedgerEntry["action"], ExecutionTimelineEventKind> = {
    create: "holdback_created",
    release: "holdback_released",
    pause: "holdback_paused",
    resume: "holdback_resumed",
    reverse: "holdback_reversed",
  };
  holdbackLedger
    .filter((h) => h.jobId === job.id)
    .forEach((h) => {
      events.push({
        id: h.id,
        jobId: job.id,
        kind: holdbackKindMap[h.action],
        at: h.createdAt,
        actorId: h.actorId,
        label: `Holdback ${h.action}`,
        detail: sanitize(h.reason),
      });
    });

  // 6) Dispute events
  const disputeKindMap: Record<DisputeEvent["type"], ExecutionTimelineEventKind> = {
    open: "dispute_opened",
    evidence_added: "dispute_evidence_added",
    withdraw: "dispute_withdrawn",
    resolve: "dispute_resolved",
  };
  disputeEvents
    .filter((d) => d.jobId === job.id)
    .forEach((d) => {
      events.push({
        id: d.id,
        jobId: job.id,
        kind: disputeKindMap[d.type],
        at: d.createdAt,
        actorId: d.actorId,
        label:
          d.type === "open"
            ? "Dispute opened"
            : d.type === "evidence_added"
              ? "Dispute evidence added"
              : d.type === "withdraw"
                ? "Dispute withdrawn"
                : `Dispute resolved${d.outcome ? ` (${d.outcome})` : ""}`,
        detail: sanitize(d.reason),
      });
    });

  // Stable chronological ordering. Stable id-tiebreak avoids flicker.
  return events.sort((a, b) => {
    const t = new Date(a.at).getTime() - new Date(b.at).getTime();
    if (t !== 0) return t;
    return a.id.localeCompare(b.id);
  });
}
